import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApproverAssignments } from "@/components/admin/ApproverAssignments";
import { EmailSettingCard, toRecipients } from "@/components/admin/EmailSettingCard";
import { ScheduleSettings } from "@/components/admin/ScheduleSettings";

const ORDER = ["welcome", "reset", "submission", "reminder", "monthly", "month_ready", "month_submission", "month_hours"];

export default function AdminConsole() {
  const [tab, setTab] = useState("approvers");
  const [settings, setSettings] = useState(null);
  const [rcpt, setRcpt] = useState({});
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setSettings(data);
    setRcpt(Object.fromEntries(ORDER.map((k) => [k, (data.emails[k].recipients || []).join(", ")])));
  };
  useEffect(() => { api.get("/admin/settings").then(({ data }) => apply(data)); }, []);

  const setEmail = (k, patch) => setSettings((s) => ({ ...s, emails: { ...s.emails, [k]: { ...s.emails[k], ...patch } } }));
  const setSchedule = (patch) => setSettings((s) => ({ ...s, schedule: { ...s.schedule, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const emails = Object.fromEntries(ORDER.map((k) => [k, { ...settings.emails[k], recipients: toRecipients(rcpt[k] || "") }]));
      const { data } = await api.put("/admin/settings", { emails, schedule: settings.schedule });
      apply(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Admin</h1>
          <p className="mt-1 text-muted-foreground">Who approves what, which emails go out, and when.</p>
        </div>
        {tab !== "approvers" && settings && (
          <Button data-testid="admin-save-btn" onClick={save} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        )}
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="approvers" data-testid="admin-tab-approvers">Approvers</TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails">Emails</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="admin-tab-schedule">Schedule</TabsTrigger>
        </TabsList>
        <TabsContent value="approvers" className="mt-4"><ApproverAssignments /></TabsContent>
        {!settings ? (
          tab !== "approvers" && <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <>
            <TabsContent value="emails" className="mt-4 space-y-4">
              {ORDER.map((k) => (
                <EmailSettingCard key={k} k={k} meta={settings.meta[k]} cfg={settings.emails[k]} recipientsText={rcpt[k] || ""}
                  onChange={(p) => setEmail(k, p)} onRecipients={(v) => setRcpt((r) => ({ ...r, [k]: v }))} />
              ))}
            </TabsContent>
            <TabsContent value="schedule" className="mt-4"><ScheduleSettings schedule={settings.schedule} onChange={setSchedule} /></TabsContent>
          </>
        )}
      </Tabs>
    </Layout>
  );
}
